import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import GUI from 'lil-gui'
import earthVertexShader from './shaders/earth/vertex.glsl'
import earthFragmentShader from './shaders/earth/fragment.glsl'

import atmosphereVertexShader from './shaders/atmosphere/vertex.glsl'
import atmosphereFragmentShader from './shaders/atmosphere/fragment.glsl'
import { Lensflare, LensflareElement } from 'three/addons/objects/Lensflare.js'


/**
 * Base
 */
// Debug
const gui = new GUI()

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

// Loaders
const textureLoader = new THREE.TextureLoader()


const earthParameters = {}
earthParameters.atmosphereDayColor = '#00aaff'
earthParameters.atmosphereTwilightColor = '#ff6600'

gui.addColor(earthParameters,'atmosphereDayColor')
    .onChange(()=>{
        earthMaterial.uniforms.uAtmosphereDayColor.value.set(earthParameters.atmosphereDayColor)
        atmosphereMaterial.uniforms.uAtmosphereDayColor.value.set(earthParameters.atmosphereDayColor)

    })

gui.addColor(earthParameters,'atmosphereTwilightColor')
.onChange(()=>{
    earthMaterial.uniforms.uAtmosphereTwilightColor.value.set(earthParameters.atmosphereTwilightColor)
    atmosphereMaterial.uniforms.uAtmosphereTwilightColor.value.set(earthParameters.atmosphereTwilightColor)
    
})

const earthDayTexture = textureLoader.load('./earth/day.jpg')
earthDayTexture.colorSpace = THREE.SRGBColorSpace
earthDayTexture.anisotropy = 8

const earthNightTexture = textureLoader.load('./earth/night.jpg')
earthNightTexture.colorSpace = THREE.SRGBColorSpace
earthNightTexture.anisotropy = 8


const earthSpecularCloudsTexture = textureLoader.load('./earth/specularClouds.jpg')
earthSpecularCloudsTexture.anisotropy = 8


/**
 * Earth
 */
// Mesh
const earthGeometry = new THREE.SphereGeometry(2, 64, 64)
const earthMaterial = new THREE.ShaderMaterial({
    vertexShader: earthVertexShader,
    fragmentShader: earthFragmentShader,
    uniforms:
    {
        uDayTexture : new THREE.Uniform(earthDayTexture),
        uNightTexture : new THREE.Uniform(earthNightTexture),
        uSpecularCloudsTexture : new THREE.Uniform(earthSpecularCloudsTexture),
        uSunDirection : new THREE.Uniform(new THREE.Vector3(0,0,1)),
        uAtmosphereDayColor : new THREE.Uniform(new THREE.Color(earthParameters.atmosphereDayColor)),
        uAtmosphereTwilightColor : new THREE.Uniform(new THREE.Color(earthParameters.atmosphereTwilightColor))
    }
})
const earth = new THREE.Mesh(earthGeometry, earthMaterial)
scene.add(earth)


const atmosphereMaterial = new THREE.ShaderMaterial({
    side:THREE.BackSide,
    transparent:true,
    vertexShader: atmosphereVertexShader,
    fragmentShader: atmosphereFragmentShader,
    uniforms:{
        uSunDirection : new THREE.Uniform(new THREE.Vector3(0,0,1)),
        uAtmosphereDayColor : new THREE.Uniform(new THREE.Color(earthParameters.atmosphereDayColor)),
        uAtmosphereTwilightColor : new THREE.Uniform(new THREE.Color(earthParameters.atmosphereTwilightColor))
    }
})
const atmosphere = new THREE.Mesh(earthGeometry,atmosphereMaterial)
atmosphere.scale.set(1.04,1.04,1.04)
scene.add(atmosphere)

const sunSpherical = new THREE.Spherical(1, Math.PI *0.5, 0.5 )
const sunDirection= new THREE.Vector3()

let debugSunTexture = textureLoader.load('/lenses/lensflare0.png')
let debugAlphaSunTexture = textureLoader.load('/lenses/lensflare1.png')

const lensflare = new Lensflare()
const textureFlare0 = textureLoader.load('/lenses/lensflare0.png')
const textureFlare1 = textureLoader.load('/lenses/lensflare1.png')

lensflare.addElement(new LensflareElement(textureFlare0, 512, 0))
lensflare.addElement(new LensflareElement(textureFlare1, 60, 0.6))
lensflare.addElement(new LensflareElement(textureFlare1, 70, 0.7))
lensflare.addElement(new LensflareElement(textureFlare1, 120, 0.9))
scene.add(lensflare)

let debugSun = lensflare

// scene.add(debugSun)
const updateSun = ()=>{
    sunDirection.setFromSpherical(sunSpherical)

    debugSun.position
    .copy(sunDirection)
    .multiplyScalar(50)

    earth.material.uniforms.uSunDirection.value.copy(sunDirection)
    atmosphere.material.uniforms.uSunDirection.value.copy(sunDirection)
}
updateSun()

gui.add(sunSpherical,'phi')
    .min(0)
    .max(Math.PI)
    .onChange(updateSun)

gui.add(sunSpherical,'theta')
    .min(-Math.PI)
    .max(Math.PI)
    .onChange(updateSun)

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight,
    pixelRatio: Math.min(window.devicePixelRatio, 2)
}

// const spaceTexture = textureLoader.load('/space2.jpg')
// spaceTexture.colorSpace = THREE.SRGBColorSpace
// spaceTexture.wrapS = THREE.RepeatWrapping
// spaceTexture.wrapT = THREE.RepeatWrapping


// spaceTexture.repeat.set(16, 16)
// // scene.background = spaceTexture
// const spaceGeometry = new THREE.SphereGeometry(51, 64, 64)
// const spaceMaterial = new THREE.MeshBasicMaterial({
//   map: spaceTexture,
//   side: THREE.BackSide,
// //   wireframe:true
// })
// const spaceMesh = new THREE.Mesh(spaceGeometry, spaceMaterial)
// scene.add(spaceMesh)

const starGeometry = new THREE.BufferGeometry()
const starCount = 10000
const starPositions = new Float32Array(starCount * 3)

for (let i = 0; i < starCount * 3; i++) {
    const r = 300 + Math.random() * 20
    const theta = Math.random() * 2 * Math.PI
    const phi = Math.acos(2 * Math.random() - 1)
    const x = r * Math.sin(phi) * Math.cos(theta)
    const y = r * Math.sin(phi) * Math.sin(theta)
    const z = r * Math.cos(phi)
    starPositions[i] = i % 3 === 0 ? x : i % 3 === 1 ? y : z
}

starGeometry.setAttribute('position', new THREE.BufferAttribute(starPositions, 3))

const starMaterial = new THREE.PointsMaterial({ color: 0xffffff, size: 0.3 })
const starField = new THREE.Points(starGeometry, starMaterial)
scene.add(starField)


window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight
    sizes.pixelRatio = Math.min(window.devicePixelRatio, 2)

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(sizes.pixelRatio)
})

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(25, sizes.width / sizes.height, 0.1, 100)
camera.position.x = 12
camera.position.y = 5
camera.position.z = 4
scene.add(camera)

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true


/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(sizes.pixelRatio)
renderer.setClearColor('#000011')




/**
 * Animate
 */
const clock = new THREE.Clock()

const tick = () =>
{
    const elapsedTime = clock.getElapsedTime()

    earth.rotation.y = elapsedTime * 0.1

    // Update controls
    controls.update()

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()